// Copyright (c) 2026 Michael F. Kamprath
//
// screen-flash.js -- whole-screen white flash overlay for the
// cloud-tap lightning reaction (slice 2.5.2).
//
// Mirrors the legacy iOS BackgroundView.m `flashLightningFor:`
// behaviour: a full-scene white rectangle whose alpha is animated
// through four keyframes to simulate a lightning strike:
//
//   0 --(flashIn)--> 0.9 --(flashOut)--> 0.3 --(flashBack)--> 0.6 --(decay)--> 0
//
// Legacy proportions of the total display duration (capped at 1.0s):
//   flashIn    = 0.20 * displayDuration   (rising from 0 to 0.9)
//   flashOut   = 0.10 * displayDuration   (falling to 0.3)
//   flashBack  = 0.05 * displayDuration   (rising to 0.6)
//   decay      = 0.65 * displayDuration   (falling to 0)
//
// The caller owns the container (we add our own sprite into it) and
// is responsible for ensuring that container renders ABOVE all other
// scene layers -- scene.js adds a dedicated top-most
// `lightningFlashContainer` expressly for this purpose.

import { Graphics } from 'pixi.js';
import { animateOver } from './entrance.js';

const MAX_DURATION_MS = 1000;   // legacy kMaxDuration = 1.0 seconds

/**
 * @typedef {Object} ScreenFlashHandle
 * @property {(durationMs?: number) => Promise<void>} flash
 *   Run the 4-keyframe flash animation over the given duration (ms).
 *   If omitted or > MAX_DURATION_MS, duration is capped.  Re-entrant
 *   calls cancel the in-flight flash and start a fresh one.
 * @property {() => void} destroy
 *   Cancel any in-flight animation, remove and destroy the graphic.
 */

/**
 * Build a screen-flash handle bound to the given container.
 *
 * The white rectangle is drawn once at construction time (covering
 * the design area) with alpha 0 so it's invisible until `flash()`
 * is invoked.  Keeping the graphic in the display list the whole
 * time lets the alpha animation run without add/remove churn per
 * flash, and matches the legacy "subview already allocated, just
 * toggle alpha" pattern.
 *
 * @param {{
 *   container: import('pixi.js').Container,
 *   designW: number,
 *   designH: number,
 * }} opts
 * @returns {ScreenFlashHandle}
 */
export function createScreenFlash({ container, designW, designH }) {
  const gfx = new Graphics();
  gfx.rect(0, 0, designW, designH).fill(0xffffff);
  gfx.alpha = 0;
  gfx.eventMode = 'none';
  container.addChild(gfx);

  let activeCancel = null;
  let destroyed = false;

  async function flash(durationMsIn) {
    if (destroyed) return;
    // Cancel any in-flight flash so a second cloud tap during the
    // decay phase restarts cleanly instead of multiplying alphas.
    if (activeCancel) activeCancel.cancelled = true;
    const cancel = { cancelled: false };
    activeCancel = cancel;

    const total = Math.min(
      Number.isFinite(durationMsIn) ? durationMsIn : MAX_DURATION_MS,
      MAX_DURATION_MS,
    );
    const flashInMs   = total * 0.20;
    const flashOutMs  = total * 0.10;
    const flashBackMs = total * 0.05;
    const decayMs     = total * 0.65;

    const tween = (durationMs, fromA, toA) => animateOver(durationMs, (t) => {
      if (destroyed) return;
      gfx.alpha = fromA + (toA - fromA) * t;
    }, cancel);

    await tween(flashInMs, 0.0, 0.9);
    if (cancel.cancelled || destroyed) return;
    await tween(flashOutMs, 0.9, 0.3);
    if (cancel.cancelled || destroyed) return;
    await tween(flashBackMs, 0.3, 0.6);
    if (cancel.cancelled || destroyed) return;
    await tween(decayMs, 0.6, 0.0);
    if (cancel.cancelled || destroyed) return;
    gfx.alpha = 0;
    if (activeCancel === cancel) activeCancel = null;
  }

  function destroy() {
    if (destroyed) return;
    destroyed = true;
    if (activeCancel) { activeCancel.cancelled = true; activeCancel = null; }
    try { gfx.parent?.removeChild(gfx); } catch { /* already gone */ }
    try { gfx.destroy(); } catch { /* already destroyed */ }
  }

  return { flash, destroy };
}
